/**
 * A workflow in the sample workspace (Calder & Wynn, fixture-calder.ts) and
 * one sample run of it, replayed in its pane: the weekly aged-receivables
 * review the firm's agent built, its six steps, each step's trace as the
 * sample records it and the result each step recorded. Plain data and a
 * timeline, like a replay's, so a server-rendered first frame can hand the
 * run to its player (workflow-run-player.tsx).
 *
 * The result is the receivables story's (groups/receivables-story.ts): its
 * invented customers and sixteen open invoices, aged as its answer ages them,
 * as at the morning the invoice snapshot was synced; the sample runs that
 * morning. Every name is invented; none is a customer, prospect or demo
 * tenant. workflow-run.test.ts checks the result against the story's answer
 * and against the application's own schemas, column inference and
 * formatting.
 */

import type { Workflow, WorkflowDefinition, WorkflowNodeTrace, WorkflowSampleResponse } from "@mlx/contracts";
import { runFromSample, type RunView } from "@product/components/app/workflows/workflow-trace-model";

import { INVOICES, RECEIVABLES_NOW, printedPounds, type Invoice } from "../editorial/bands/receivables-story";

/* ------------------------------------------------------------------ */
/*  The workflow                                                        */
/* ------------------------------------------------------------------ */

/** The ageing buckets, as the answer heads its columns. */
const BUCKETS = ["Current", "1–30 days", "31–60 days", "61–90 days", "91+ days"] as const;
type Bucket = (typeof BUCKETS)[number];

/** The sources' queries, as the workflow's code writes them and each source records them. */
const XERO_SQL =
  "SELECT invoice_number, contact_id, due_date, amount_due, synced_on FROM INVOICES WHERE type = 'ACCREC' AND amount_due > 0 AND status NOT IN ('VOIDED', 'DELETED') ORDER BY due_date";
const HUBSPOT_SQL = "SELECT company_id, name, xero_contact_id FROM COMPANIES WHERE xero_contact_id IS NOT NULL ORDER BY name";

const PYTHON = `from collections import defaultdict
from datetime import date

BUCKETS = ["Current", "1–30 days", "31–60 days", "61–90 days", "91+ days"]


XERO_SQL = ${JSON.stringify(XERO_SQL)}
HUBSPOT_SQL = ${JSON.stringify(HUBSPOT_SQL)}


def hubspot_companies(inputs):
    return {"tool": "warehouse_query", "arguments": {"rowLimit": 200, "sql": HUBSPOT_SQL}}


def xero_invoices(inputs):
    return {"tool": "warehouse_query", "arguments": {"rowLimit": 200, "sql": XERO_SQL}}


def match_customers(inputs):
    companies = {company["xero_contact_id"]: company for company in inputs["hubspot_companies"]}
    invoices = defaultdict(list)
    for invoice in inputs["xero_invoices"]:
        invoices[invoice["contact_id"]].append(invoice["invoice_number"])
    return [{"company_id": companies[contact]["company_id"], "customer": companies[contact]["name"],
             "xero_contact_id": contact, "invoices": numbers} for contact, numbers in invoices.items()]


def age_invoices(inputs):
    aged = []
    for invoice in inputs["xero_invoices"]:
        days = (date.fromisoformat(invoice["synced_on"]) - date.fromisoformat(invoice["due_date"])).days
        bucket = 0 if days <= 0 else min(4, (days - 1) // 30 + 1)
        aged.append({**invoice, "days_overdue": max(days, 0), "bucket": BUCKETS[bucket]})
    return aged


def summarise_finance(inputs):
    aged = {invoice["invoice_number"]: invoice for invoice in inputs["age_invoices"]}
    customers = []
    for customer in inputs["match_customers"]:
        invoices = [aged[number] for number in customer["invoices"]]
        buckets = dict.fromkeys(BUCKETS, 0)
        for invoice in invoices:
            buckets[invoice["bucket"]] = round(buckets[invoice["bucket"]] + invoice["amount_due"], 2)
        customers.append({"customer": customer["customer"], "invoices": len(invoices), "buckets": buckets,
                          "outstanding": round(sum(invoice["amount_due"] for invoice in invoices), 2)})
    shown = [bucket for bucket in BUCKETS if any(customer["buckets"][bucket] for customer in customers)]
    ranked = sorted(customers, key=lambda customer: (-customer["outstanding"], customer["customer"]))
    return [{"Customer": customer["customer"], "Invoices": customer["invoices"],
             "Outstanding": f"£{customer['outstanding']:,.2f}",
             **{bucket: f"£{customer['buckets'][bucket]:,.2f}" for bucket in shown}} for customer in ranked]


def check_totals(inputs):
    invoices = inputs["age_invoices"]
    unaged = [invoice["invoice_number"] for invoice in invoices if invoice["bucket"] not in BUCKETS]
    if unaged:
        raise ValueError(f"No ageing bucket for {', '.join(unaged)}")
    outstanding = round(sum(invoice["amount_due"] for invoice in invoices), 2)
    buckets = {bucket: round(sum(invoice["amount_due"] for invoice in invoices if invoice["bucket"] == bucket), 2)
               for bucket in BUCKETS}
    if round(sum(buckets.values()), 2) != outstanding:
        raise ValueError("The buckets do not add up to the invoice balances")
    return {"reconciled": True, "invoices": len(invoices), "outstanding": outstanding, "buckets": buckets}
`;

const STEP_IDS = [
  "hubspot_companies",
  "xero_invoices",
  "match_customers",
  "age_invoices",
  "summarise_finance",
  "check_totals",
] as const;
type StepId = (typeof STEP_IDS)[number];
type WorkflowStep = Omit<WorkflowDefinition["nodes"][number], "id" | "inputs"> & { id: StepId; inputs: StepId[] };

/**
 * The steps, in the order the sample runs them. The canvas lays them out by
 * dependency, three rows of two: the sources, then matching and ageing, then
 * the summary beside the check that its buckets reconcile.
 */
const NODES: readonly WorkflowStep[] = [
  {
    id: "hubspot_companies",
    label: "Read HubSpot companies",
    kind: "source",
    description: "HubSpot companies, with the Xero contact the Twin has matched to each.",
    inputs: [],
  },
  {
    id: "xero_invoices",
    label: "Read Xero invoices",
    kind: "source",
    description: "Open sales invoices synced from Xero: each one's contact, due date and amount due.",
    inputs: [],
  },
  {
    id: "match_customers",
    label: "Match customers",
    kind: "transform",
    description: "Groups the invoices by customer, using the Twin's accepted matches between Xero and HubSpot.",
    inputs: ["hubspot_companies", "xero_invoices"],
  },
  {
    id: "age_invoices",
    label: "Age each invoice",
    kind: "transform",
    description: "Buckets each invoice by days past its due date: current, 1–30, 31–60, 61–90 or 91+.",
    inputs: ["xero_invoices"],
  },
  {
    id: "summarise_finance",
    label: "Summarise for Finance",
    kind: "output",
    description: "Ranks customers by balance, with each one's amounts by age, for the Finance channel.",
    inputs: ["match_customers", "age_invoices"],
  },
  {
    id: "check_totals",
    label: "Check totals reconcile",
    kind: "rule",
    description: "Every invoice must land in one bucket, and the buckets must add up to the invoice balances.",
    inputs: ["age_invoices"],
  },
];

/** Built by the agent a few minutes before its first sample, and not yet tested. */
export const WORKFLOW: Workflow = {
  id: "wf_weekly_receivables",
  sessionId: "ses_weekly_receivables",
  version: 1,
  digest: "sha256:6f1c2a9e4b7d3058",
  updatedAt: RECEIVABLES_NOW - 4 * 60_000,
  title: null,
  archivedAt: null,
  definition: {
    schemaVersion: 1,
    title: "Weekly aged receivables",
    description:
      "Ages the open Xero invoices by customer, checks the totals reconcile to the invoice balances and ranks each customer's balance for the Finance channel.",
    python: PYTHON,
    nodes: [...NODES],
  },
  preview: null,
  previewRun: null,
};

/** The workflow's name as the pane shows it: the agent's title, as no one has renamed it. */
export const WORKFLOW_TITLE = WORKFLOW.title ?? WORKFLOW.definition.title;

/* ------------------------------------------------------------------ */
/*  What each step recorded                                             */
/* ------------------------------------------------------------------ */

/** The snapshot's date: the invoices are aged as at the morning they were synced. */
const SYNCED_ON = new Date(RECEIVABLES_NOW).toISOString().slice(0, 10);
const DAY = 86_400_000;

const CUSTOMERS = [...new Set(INVOICES.map((invoice) => invoice.customer))].sort();
const contactId = (customer: string) => `XC-${String(CUSTOMERS.indexOf(customer) + 101)}`;
const companyId = (customer: string) => `HS-${String(CUSTOMERS.indexOf(customer) + 4401)}`;

function bucketOf(invoice: Invoice): { daysOverdue: number; bucket: Bucket } {
  const days = Math.round((Date.parse(`${SYNCED_ON}T00:00:00Z`) - Date.parse(`${invoice.dueDate}T00:00:00Z`)) / DAY);
  const index = days <= 0 ? 0 : Math.min(4, Math.floor((days - 1) / 30) + 1);
  return { daysOverdue: Math.max(days, 0), bucket: BUCKETS[index]! };
}

const synced = INVOICES.map((invoice) => ({
  invoice_number: invoice.number,
  contact_id: contactId(invoice.customer),
  due_date: invoice.dueDate,
  amount_due: invoice.amountDuePence / 100,
  synced_on: SYNCED_ON,
}));

const companies = CUSTOMERS.map((customer) => ({
  company_id: companyId(customer),
  name: customer,
  xero_contact_id: contactId(customer),
}));

const aged = INVOICES.map((invoice, index) => {
  const { daysOverdue, bucket } = bucketOf(invoice);
  return { ...synced[index]!, days_overdue: daysOverdue, bucket };
});

const matched = [...new Set(INVOICES.map((invoice) => invoice.customer))].map((customer) => ({
  company_id: companyId(customer),
  customer,
  xero_contact_id: contactId(customer),
  invoices: INVOICES.filter((invoice) => invoice.customer === customer).map((invoice) => invoice.number),
}));

/** Each customer's invoices, balance and balance by bucket, in pence so the sums are exact. */
const totals = matched.map(({ customer }) => {
  const own = INVOICES.filter((invoice) => invoice.customer === customer);
  const pence = Object.fromEntries(BUCKETS.map((bucket) => [bucket, 0])) as Record<Bucket, number>;
  for (const invoice of own) pence[bucketOf(invoice).bucket] += invoice.amountDuePence;
  return { customer, invoices: own.length, pence: own.reduce((sum, invoice) => sum + invoice.amountDuePence, 0), bucketPence: pence };
});

const outstandingPence = INVOICES.reduce((sum, invoice) => sum + invoice.amountDuePence, 0);

/** The check's record: every invoice in a bucket, and the buckets adding up to the balances. */
const checked = {
  reconciled: true,
  invoices: INVOICES.length,
  outstanding: outstandingPence / 100,
  buckets: Object.fromEntries(
    BUCKETS.map((bucket) => [
      bucket,
      INVOICES.filter((invoice) => bucketOf(invoice).bucket === bucket).reduce((sum, invoice) => sum + invoice.amountDuePence, 0) / 100,
    ]),
  ),
};

/** The buckets any customer has a balance in; the summary leaves the empty ones out. */
const SHOWN_BUCKETS = BUCKETS.filter((bucket) => totals.some((total) => total.bucketPence[bucket] > 0));

/**
 * The workflow's output: each customer ranked by balance, largest first
 * (ties by name), with its invoice count and its balance by age, the amounts
 * printed as the step prints them.
 */
export const WORKFLOW_OUTPUT: readonly Record<string, string | number>[] = [...totals]
  .sort((a, b) => b.pence - a.pence || a.customer.localeCompare(b.customer))
  .map((total) => ({
    Customer: total.customer,
    Invoices: total.invoices,
    Outstanding: printedPounds(total.pence),
    ...Object.fromEntries(SHOWN_BUCKETS.map((bucket) => [bucket, printedPounds(total.bucketPence[bucket])])),
  }));

const sourceRequest = (sql: string) => ({ tool: "warehouse_query", arguments: { rowLimit: 200, sql } });

/** What each step recorded: a source its request beside the rows it read, every other step its output. */
const OUTPUTS: Readonly<Record<StepId, unknown>> = {
  hubspot_companies: { request: sourceRequest(HUBSPOT_SQL), response: companies },
  xero_invoices: { request: sourceRequest(XERO_SQL), response: synced },
  match_customers: matched,
  age_invoices: aged,
  summarise_finance: WORKFLOW_OUTPUT,
  check_totals: checked,
};

/**
 * The output as the Results tab lays it out: a column per key, named and
 * typed as the application infers them from the rows (outputFields), and
 * every value as the grid prints it (formatFieldValue).
 */
export type WorkflowResultColumn = { key: string; label: string; type: "text" | "number" };
export const WORKFLOW_RESULT_COLUMNS: readonly WorkflowResultColumn[] = Object.keys(WORKFLOW_OUTPUT[0]!).map((key) => ({
  key,
  label: key,
  type: typeof WORKFLOW_OUTPUT[0]![key] === "number" ? "number" : "text",
}));
export const WORKFLOW_RESULT_ROWS: readonly Readonly<Record<string, string>>[] = WORKFLOW_OUTPUT.map((row) =>
  Object.fromEntries(Object.entries(row).map(([key, value]) => [key, String(value)])),
);

/* ------------------------------------------------------------------ */
/*  The sample                                                          */
/* ------------------------------------------------------------------ */

/** How long each step takes in the sample, in order: each starts as the one before completes. */
const STEP_DURATIONS: Readonly<Record<StepId, number>> = {
  hubspot_companies: 1_700,
  xero_invoices: 2_400,
  match_customers: 2_220,
  age_invoices: 480,
  summarise_finance: 760,
  check_totals: 310,
};

/**
 * The replay's moments, in ms from its start: the workflow at rest, Test
 * sample pressed, the sample created (the pane follows it from here), the
 * session listing its run, and the first step starting. The steps then take
 * their own time; the Results tab opens a moment after the last completes.
 */
const MOMENTS = { press: 2_000, created: 2_600, listed: 3_300, firstStep: 3_700 } as const;
const RESULTS_AFTER_MS = 2_000;
const RESULTS_HOLD_MS = 5_600;

/** The fixture's clock at the replay's start: the morning the invoices were synced (RECEIVABLES_NOW). */
const CLOCK_START = RECEIVABLES_NOW;
const SAMPLE_RUN_ID = "run_weekly_receivables_sample_1";

/** When each step starts and completes, in ms from the replay's start. */
const STEPS: readonly { id: StepId; startMs: number; endMs: number }[] = (() => {
  let atMs: number = MOMENTS.firstStep;
  return NODES.map(({ id }) => {
    const step = { id, startMs: atMs, endMs: atMs + STEP_DURATIONS[id] };
    atMs = step.endMs;
    return step;
  });
})();

const COMPLETED_MS = STEPS.at(-1)!.endMs;

function traceValue(value: unknown): WorkflowNodeTrace["input"] {
  return { value: value as WorkflowNodeTrace["input"]["value"], bytes: new TextEncoder().encode(JSON.stringify(value)).length, omitted: false };
}

function inputsOf(id: StepId): Record<string, unknown> {
  const node = NODES.find((entry) => entry.id === id)!;
  return Object.fromEntries(
    node.inputs.map((input) => {
      const output = OUTPUTS[input];
      // A source's rows reach the next step; its request stays with its own trace.
      return [input, NODES.find((entry) => entry.id === input)!.kind === "source" ? (output as { response: unknown }).response : output];
    }),
  );
}

/** Each step's trace up to `atMs`: none before it starts, running, then completed with its output. */
function tracesAt(atMs: number): WorkflowNodeTrace[] {
  return STEPS.filter((step) => step.startMs <= atMs).map((step) => {
    const done = step.endMs <= atMs;
    return {
      nodeId: step.id,
      status: done ? "succeeded" : "running",
      startedAt: CLOCK_START + step.startMs,
      completedAt: done ? CLOCK_START + step.endMs : null,
      input: traceValue(inputsOf(step.id)),
      output: done ? traceValue(OUTPUTS[step.id]) : null,
      error: null,
      judgmentSkipped: false,
      skippedEffects: [],
    };
  });
}

const PREVIEW: NonNullable<Workflow["preview"]> = {
  version: WORKFLOW.version,
  status: "succeeded",
  nodes: NODES.map(({ id }) => ({ id, output: OUTPUTS[id] as NonNullable<Workflow["preview"]>["nodes"][number]["output"] })),
  error: null,
};

/** The workflow once the sample has recorded its result. */
export const WORKFLOW_TESTED: Workflow = {
  ...WORKFLOW,
  preview: PREVIEW,
  previewRun: { id: SAMPLE_RUN_ID, createdAt: CLOCK_START + MOMENTS.created },
};

function sampleAt(atMs: number): WorkflowSampleResponse {
  const completed = atMs >= COMPLETED_MS;
  const nodes = tracesAt(atMs);
  return {
    runId: SAMPLE_RUN_ID,
    workflowId: WORKFLOW.id,
    version: WORKFLOW.version,
    status: completed ? "succeeded" : nodes.length ? "running" : "queued",
    preview: completed ? PREVIEW : null,
    error: null,
    nodes,
  };
}

/* ------------------------------------------------------------------ */
/*  The replay                                                          */
/* ------------------------------------------------------------------ */

/** The pane at one moment of the replay: what each of its views reads. */
export type WorkflowRunFrame = {
  /** When the frame starts, in ms from the replay's start. */
  atMs: number;
  /** The pane's tab: the canvas while the sample runs, then Results. */
  tab: "canvas" | "results";
  /** The workflow as the pane holds it: untested until the sample records its result. */
  workflow: Workflow;
  /** The sample the pane follows, from the moment it is created. */
  sample: WorkflowSampleResponse | null;
  /** The sample as the canvas and the run banner show it (runFromSample). */
  run: RunView | null;
  /** Test sample was pressed and the session has not yet listed the run. */
  starting: boolean;
  /** The session is running the sample. */
  running: boolean;
  /** The fixture's clock at the frame, which the pane's ages are read against. */
  now: number;
};

export type WorkflowRunTimeline = {
  /** One frame per change the pane shows, in order. */
  frames: readonly WorkflowRunFrame[];
  /** When the last step completes. */
  completedMs: number;
  /** When the Results tab opens. */
  resultsMs: number;
  endsMs: number;
};

function frame(atMs: number, state: Partial<Omit<WorkflowRunFrame, "atMs" | "run" | "now">> = {}): WorkflowRunFrame {
  const workflow = state.workflow ?? WORKFLOW;
  const sample = state.sample ?? null;
  return {
    atMs,
    tab: "canvas",
    workflow,
    sample,
    run: sample ? runFromSample(sample, workflow) : null,
    starting: false,
    running: false,
    now: CLOCK_START + atMs,
    ...state,
  };
}

function workflowRunTimeline(): WorkflowRunTimeline {
  const resultsMs = COMPLETED_MS + RESULTS_AFTER_MS;
  const queued = sampleAt(MOMENTS.created);
  const done = sampleAt(COMPLETED_MS);
  return {
    frames: [
      frame(0),
      frame(MOMENTS.press, { starting: true }),
      frame(MOMENTS.created, { starting: true, running: true, sample: queued }),
      frame(MOMENTS.listed, { running: true, sample: queued }),
      ...STEPS.map((step) => frame(step.startMs, { running: true, sample: sampleAt(step.startMs) })),
      frame(COMPLETED_MS, { workflow: WORKFLOW_TESTED, sample: done }),
      frame(resultsMs, { tab: "results", workflow: WORKFLOW_TESTED, sample: done }),
    ],
    completedMs: COMPLETED_MS,
    resultsMs,
    endsMs: resultsMs + RESULTS_HOLD_MS,
  };
}

/**
 * The sample run replayed: the workflow at rest on its canvas, Test sample
 * pressed, each step waiting, running and completing in turn, then the
 * Results tab with the workflow's output.
 */
export const WORKFLOW_RUN: WorkflowRunTimeline = workflowRunTimeline();

/** The frame on screen `atMs` into the replay; the same object throughout its span. */
export function workflowRunFrameAt(atMs: number, timeline: WorkflowRunTimeline = WORKFLOW_RUN): WorkflowRunFrame {
  return [...timeline.frames].reverse().find((entry) => entry.atMs <= atMs) ?? timeline.frames[0]!;
}
